import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { trackIntent } from '../utils/metaPixel';
interface HeroSectionProps {
  onOpenForm: () => void;
}
export function HeroSection({
  onOpenForm
}: HeroSectionProps) {
  const scrollToMethodology = () => {
    const footer = document.querySelector('footer');
    footer?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <section className="relative bg-white pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden" data-id="element-187">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" data-id="element-188">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(#0F2747 1px, transparent 1px)',
        backgroundSize: '32px 32px'
      }} data-id="element-189"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-190">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-12 items-center" data-id="element-191">
          {/* Left Column: Copy */}
          <div className="max-w-2xl text-left" data-id="element-192">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.05
          }} className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald/10 border border-emerald/20 mb-8" data-id="element-193">
              <span className="text-emerald text-xs font-bold tracking-widest uppercase" data-id="element-194">
                Credit Data Integrity
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-navy leading-[1.1] mb-6 tracking-tight" data-id="element-195">
              The Bureau Data
              <br data-id="element-196" />
              Most Consumers Never Audit
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-lg sm:text-xl text-slate mb-4 leading-[1.6] font-medium max-w-xl" data-id="element-197">
              Learn the federal rights, reporting gaps, and dispute workflows
              buried inside the consumer credit system.
            </motion.p>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="text-sm text-gold italic mb-10 max-w-xl" data-id="element-198">
              The goal is not panic. It is visibility.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-199">
              <button onClick={() => {
              trackIntent('hero_playbook');
              onOpenForm();
            }} className="w-full sm:w-auto bg-emerald hover:bg-emerald-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-cta hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center" data-id="element-200">
                Get The Playbook
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-201" />
              </button>
              <button onClick={scrollToMethodology} className="w-full sm:w-auto text-navy font-bold text-lg hover:text-emerald transition-colors flex items-center justify-center group" data-id="element-202">
                <span className="border-b-2 border-gold pb-0.5 group-hover:border-emerald transition-colors" data-id="element-203">
                  Browse Research
                </span>
              </button>
            </motion.div>
          </div>

          {/* Right Column: Bloomberg-Terminal-Style Data Dashboard */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="relative lg:ml-auto w-full max-w-md mx-auto" data-id="element-204">
            {/* Terminal Container */}
            <div className="bg-charcoal rounded-lg p-6 shadow-2xl border border-navy-700" data-id="element-205">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-3 border-b border-navy-700" data-id="element-206">
                <span className="font-mono text-xs text-emerald" data-id="element-207">
                  BUREAU_TIMELINE_ANALYSIS
                </span>
                <span className="font-mono text-xs text-slate-400" data-id="element-208">
                  2024-Q4
                </span>
              </div>

              {/* Bureau Timelines */}
              <div className="space-y-6 mb-6" data-id="element-209">
                {/* Experian */}
                <div data-id="element-210">
                  <div className="flex items-center justify-between mb-2" data-id="element-211">
                    <span className="font-mono text-xs text-white font-bold" data-id="element-212">
                      EXPERIAN
                    </span>
                    <span className="font-mono text-xs text-slate-400" data-id="element-213">
                      Last Update: 12d ago
                    </span>
                  </div>
                  <div className="relative h-2 bg-navy-800 rounded-full overflow-hidden" data-id="element-214">
                    <div className="absolute inset-0 bg-emerald/20" style={{
                    width: '75%'
                  }} data-id="element-215"></div>
                    <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full" style={{
                    left: '45%'
                  }} data-id="element-216"></div>
                    <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full" style={{
                    left: '68%'
                  }} data-id="element-217"></div>
                  </div>
                </div>

                {/* Equifax */}
                <div data-id="element-218">
                  <div className="flex items-center justify-between mb-2" data-id="element-219">
                    <span className="font-mono text-xs text-white font-bold" data-id="element-220">
                      EQUIFAX
                    </span>
                    <span className="font-mono text-xs text-slate-400" data-id="element-221">
                      Last Update: 8d ago
                    </span>
                  </div>
                  <div className="relative h-2 bg-navy-800 rounded-full overflow-hidden" data-id="element-222">
                    <div className="absolute inset-0 bg-emerald/20" style={{
                    width: '70%'
                  }} data-id="element-223"></div>
                    <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full" style={{
                    left: '52%'
                  }} data-id="element-224"></div>
                  </div>
                </div>

                {/* TransUnion */}
                <div data-id="element-225">
                  <div className="flex items-center justify-between mb-2" data-id="element-226">
                    <span className="font-mono text-xs text-white font-bold" data-id="element-227">
                      TRANSUNION
                    </span>
                    <span className="font-mono text-xs text-slate-400" data-id="element-228">
                      Last Update: 5d ago
                    </span>
                  </div>
                  <div className="relative h-2 bg-navy-800 rounded-full overflow-hidden" data-id="element-229">
                    <div className="absolute inset-0 bg-emerald/20" style={{
                    width: '80%'
                  }} data-id="element-230"></div>
                    <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full" style={{
                    left: '38%'
                  }} data-id="element-231"></div>
                    <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full" style={{
                    left: '71%'
                  }} data-id="element-232"></div>
                  </div>
                </div>
              </div>

              {/* Annotation Panel */}
              <div className="bg-navy-800 rounded p-4 border border-navy-700" data-id="element-233">
                <div className="flex items-start space-x-3" data-id="element-234">
                  <AlertCircle className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" data-id="element-235" />
                  <div data-id="element-236">
                    <div className="font-mono text-xs text-gold font-bold mb-1" data-id="element-237">
                      CFPB COMPLAINT PATTERN #001
                    </div>
                    <p className="font-mono text-xs text-slate-300 leading-relaxed" data-id="element-238">
                      Discrepancy markers indicate mismatched reporting dates
                      across bureaus for identical accounts
                    </p>
                  </div>
                </div>
              </div>

              {/* "In Parallel" Badge - More Prominent */}
              <div className="mt-6 pt-4 border-t border-navy-700" data-id="element-239">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-center" data-id="element-240">
                  <span className="text-emerald-700 text-base font-bold leading-tight block" data-id="element-241">
                    Build Positive Reporting While Auditing Bureau Accuracy
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}